/**
 * Tabs Component
 * Componente de tabs moderno con animaciones
 */

import { useState, createContext, useContext, ReactNode } from "react";
import { cn } from "@/lib/utils";

interface TabsContextType {
  activeTab: string;
  setActiveTab: (id: string) => void;
}

const TabsContext = createContext<TabsContextType | null>(null);

function useTabs() {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error("Tab components must be used within a Tabs component");
  }
  return context;
}

interface TabsProps {
  defaultValue: string;
  value?: string;
  onValueChange?: (value: string) => void;
  children: ReactNode;
  className?: string;
}

export function Tabs({
  defaultValue,
  value,
  onValueChange,
  children,
  className,
}: TabsProps) {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const activeTab = value ?? internalValue;

  const setActiveTab = (id: string) => {
    if (!value) {
      setInternalValue(id);
    }
    onValueChange?.(id);
  };

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab }}>
      <div className={cn("w-full", className)}>{children}</div>
    </TabsContext.Provider>
  );
}

interface TabsListProps {
  children: ReactNode;
  className?: string;
  variant?: "default" | "pills" | "underline";
}

export function TabsList({ children, className, variant = "default" }: TabsListProps) {
  const variants = {
    default: "flex gap-1 p-1 bg-gray-100 rounded-xl",
    pills: "flex gap-2",
    underline: "flex gap-6 border-b border-gray-200",
  };

  return (
    <div role="tablist" className={cn(variants[variant], className)}>
      {children}
    </div>
  );
}

interface TabsTriggerProps {
  value: string;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  icon?: ReactNode;
  variant?: "default" | "pills" | "underline";
}

export function TabsTrigger({
  value,
  children,
  className,
  disabled = false,
  icon,
  variant = "default",
}: TabsTriggerProps) {
  const { activeTab, setActiveTab } = useTabs();
  const isActive = activeTab === value;

  const baseStyles = "inline-flex items-center gap-2 font-medium transition-all duration-200";
  
  const variants = {
    default: cn(
      "px-4 py-2 text-sm rounded-lg",
      isActive
        ? "bg-white text-gray-900 shadow-sm"
        : "text-gray-600 hover:text-gray-900"
    ),
    pills: cn(
      "px-4 py-2 text-sm rounded-full",
      isActive
        ? "bg-primary-600 text-white shadow-md"
        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
    ),
    underline: cn(
      "px-1 py-3 text-sm border-b-2 -mb-px",
      isActive
        ? "border-primary-600 text-primary-600"
        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
    ),
  };

  return (
    <button
      role="tab"
      aria-selected={isActive}
      aria-controls={`panel-${value}`}
      disabled={disabled}
      onClick={() => setActiveTab(value)}
      className={cn(
        baseStyles,
        variants[variant],
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}
    >
      {icon}
      {children}
    </button>
  );
}

interface TabsContentProps {
  value: string;
  children: ReactNode;
  className?: string;
}

export function TabsContent({ value, children, className }: TabsContentProps) {
  const { activeTab } = useTabs();

  if (activeTab !== value) return null;

  return (
    <div
      role="tabpanel"
      id={`panel-${value}`}
      className={cn("animate-fade-in mt-4", className)}
    >
      {children}
    </div>
  );
}

export default Tabs;
